import { Link } from "react-router";

import { OndoMark } from "../lib/OndoMark.tsx";
import { PortalOrgCard } from "./PortalOrgCard.tsx";
import { PortalUserCard } from "./PortalUserCard.tsx";
import { SHELL_COPY } from "./shell.constants.ts";
import type { NavItem, OrgCard, UserCard } from "./shell.model.ts";

/**
 * The rail: the mark, the firm, the work, the account, whoever is signed in.
 *
 * Two nav stacks rather than one list. The top is the work — your holdings and
 * what you do to them; the bottom, pushed to the foot, is the account. They are
 * separated by whitespace and not a rule, because they are two lists of the
 * same kind of thing and a line between them would claim otherwise. Which stack
 * an item belongs in is `group` on the portal's destinations, not a slice index
 * here.
 *
 * The organisation card sits ABOVE the nav and the identity card BELOW it,
 * which is the order the two answer in: what this workspace is, then what is in
 * it, then who is reading. A personal workspace has no first card and the rail
 * closes up — `org` is null and nothing reserves the space.
 */
export function PortalSidebar({
  eyebrow,
  navLabel,
  nav,
  org,
  user,
  onSignOut,
}: {
  eyebrow: string;
  navLabel: string;
  nav: NavItem[];
  org: OrgCard | null;
  user: UserCard;
  onSignOut: () => void;
}) {
  const main = nav.filter((item) => item.group === "main");
  const foot = nav.filter((item) => item.group === "foot");

  return (
    <div className="shell__sidebar">
      <div className="shell__brand">
        {/* The same four bars the signed-out screens and every viewing room
            carry, from lib/OndoMark. The text mark this replaced was three
            pipe characters, which rendered as whatever the serif felt like. */}
        <span className="shell__brand-mark" aria-hidden="true">
          <OndoMark />
        </span>
        <span className="shell__brand-name">
          ONDO
          <small>{eyebrow}</small>
        </span>
      </div>

      {org && <PortalOrgCard org={org} />}

      <nav className="shell__nav" aria-label={navLabel}>
        <ul className="shell__nav-list">
          {main.map((item) => (
            <NavRow key={item.id} item={item} />
          ))}
        </ul>

        <ul className="shell__nav-list shell__nav-list--foot">
          {foot.map((item) => (
            <NavRow key={item.id} item={item} />
          ))}
        </ul>
      </nav>

      <div className="shell__foot">
        <PortalUserCard user={user} />
        <button type="button" className="shell__signout" onClick={onSignOut}>
          {SHELL_COPY.signOut}
        </button>
      </div>
    </div>
  );
}

function NavRow({ item }: { item: NavItem }) {
  return (
    <li>
      {/* An anchor rather than a button: these are addresses now, and a
          destination you cannot middle-click or copy is not one. */}
      <Link
        to={item.href}
        className={`shell__nav-item${item.current ? " shell__nav-item--current" : ""}`}
        aria-current={item.current ? "page" : undefined}
      >
        <span className="shell__nav-glyph" aria-hidden="true">
          {item.glyph}
        </span>
        <span className="shell__nav-label">{item.label}</span>
        {/* Marked rather than disabled: the page behind it explains what
            is missing, which a greyed-out item cannot do. */}
        {item.planned && <span className="shell__nav-planned">soon</span>}
        {/* A count, not a dot: "3 waiting" is actionable and a dot is
            only a prompt to go and look. Null when there is nothing. */}
        {item.badge !== null && <span className="shell__nav-badge">{item.badge}</span>}
      </Link>
    </li>
  );
}
